import { useState, useCallback, useEffect, useRef } from "react";
import {
  hideIntercom,
  showIntercom,
  lockScroll,
  unlockScroll,
} from "../utils/Utils.js";

// disableScrollLock: pass it when the modal is rendered with Overlay or
// CenteredOverlay, which lock page scroll themselves while open. The lock is
// ref-counted, so locking from both places is harmless, just redundant.

// Tracks every currently-open modal opened through useModal, Overlay or
// CenteredOverlay (by instance id), so the Intercom launcher hides whenever
// any of them is open and only reappears once all of them are closed —
// regardless of which order they open/close in, and safe even if a modal's
// owning component unmounts while still "open". Components that call
// hideIntercom/showIntercom directly are not tracked here.
const openModalIds = new Set();

export function registerModalOpen(id) {
  const wasEmpty = openModalIds.size === 0;
  openModalIds.add(id);
  if (wasEmpty) {
    hideIntercom();
  }
}

export function registerModalClosed(id) {
  if (!openModalIds.delete(id)) return;
  if (openModalIds.size === 0) {
    // Defer: a modal closing can be immediately followed, in the same
    // synchronous flow, by another one opening (e.g. an age-check modal
    // closing while it reopens a drawer behind it). Waiting a tick means we
    // only show Intercom if we're still fully closed once that settles.
    queueMicrotask(() => {
      if (openModalIds.size === 0) {
        showIntercom();
      }
    });
  }
}

// For modal shells whose open state is controlled by props (ResponsiveModal):
// locks page scroll and hides Intercom while `open` is true, and releases
// both on unmount even if the modal was still open.
export function useModalLock(open, label = "modal") {
  const idRef = useRef();
  if (!idRef.current) {
    idRef.current = Symbol(label);
  }

  useEffect(() => {
    if (open) {
      lockScroll(idRef.current);
      registerModalOpen(idRef.current);
    } else {
      unlockScroll(idRef.current);
      registerModalClosed(idRef.current);
    }
  }, [open]);

  useEffect(
    () => () => {
      unlockScroll(idRef.current);
      registerModalClosed(idRef.current);
    },
    []
  );
}

const useModal = (initialState = false, { disableScrollLock = false } = {}) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const idRef = useRef();
  if (!idRef.current) {
    idRef.current = Symbol("modal");
  }

  useEffect(() => {
    return () => {
      unlockScroll(idRef.current);
      registerModalClosed(idRef.current);
    };
  }, []);

  const openModal = useCallback(() => {
    if (!disableScrollLock) {
      lockScroll(idRef.current);
    }
    registerModalOpen(idRef.current);
    setIsOpen(true);
  }, [disableScrollLock]);

  const closeModal = useCallback(() => {
    if (!disableScrollLock) {
      unlockScroll(idRef.current);
    }
    registerModalClosed(idRef.current);
    setIsOpen(false);
  }, [disableScrollLock]);

  const toggleModal = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal,
  };
};

export default useModal;
